import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiFehler, hole, schicke } from '../lib/api'
import { datumHeute, datumKurz } from '../lib/datum'
import { balkenAnteil, centsAusEingabe, centsFormatiert } from '../lib/geld'
import { useDarf, useSitzung } from '../session/SessionProvider'
import { useFab } from '../shell/FabSlot'
import { Button, Card, Empty, IconButton, ListItem, Sheet, TextField, useSnackbar } from '../ui'

interface BudgetZeile {
  id: string
  name: string
  limitCents: number
  startsOn: string
  visibility: string
  periodStart: string
  periodEnd: string
  laeuft: boolean
  spentCents: number
}

interface Eintrag {
  id: string
  amountCents: number
  note: string | null
  occurredOn: string
}

/**
 * Budget — je Umschlag eine Karte mit Balken, darunter die Einträge.
 *
 * ⚠️ „Verbraucht" und „übrig" kommen vom Server. Der Balken ist das Einzige,
 * was hier gerechnet wird, und das ist eine Breite, kein Betrag.
 */
export function Budget() {
  const qc = useQueryClient()
  const { abfrage } = useSitzung()
  const zeigeMeldung = useSnackbar()
  const preview = Boolean(abfrage.data && !abfrage.data.setupRequired && abfrage.data.preview)
  const darfEintragen = useDarf('budget.edit') && !preview
  const darfVerwalten = useDarf('budget.manage') && !preview
  const heute = datumHeute()

  const [neuOffen, setNeuOffen] = useState(false)
  const [neu, setNeu] = useState({ name: '', grenze: '', startsOn: heute })
  const [eintragFuer, setEintragFuer] = useState<string | null>(null)
  const [eintrag, setEintrag] = useState({ betrag: '', note: '', occurredOn: heute })
  const [fehler, setFehler] = useState<string | null>(null)
  const [offen, setOffen] = useState<string | null>(null)

  const liste = useQuery({
    queryKey: ['budget', 'list', heute],
    queryFn: () => hole<{ budgets: BudgetZeile[] }>(`/api/budget?am=${heute}`),
  })
  const budgets = liste.data?.budgets ?? []

  const einzeln = useQuery({
    queryKey: ['budget', 'eintraege', offen, heute],
    queryFn: () =>
      hole<{ budget: BudgetZeile; entries: Eintrag[] }>(`/api/budget/${offen ?? ''}?am=${heute}`),
    enabled: offen !== null,
  })

  const aktualisiere = async () => {
    await qc.invalidateQueries({ queryKey: ['budget'] })
  }

  const anlegen = useMutation({
    mutationFn: () => {
      const cents = centsAusEingabe(neu.grenze)
      if (cents === null || cents <= 0)
        throw new ApiFehler('validation', 'Bitte eine Grenze über null angeben.', 0)
      return schicke<{ budget: BudgetZeile }>('POST', '/api/budget', {
        name: neu.name,
        limitCents: cents,
        startsOn: neu.startsOn,
      })
    },
    onSuccess: async () => {
      setNeuOffen(false)
      setNeu({ name: '', grenze: '', startsOn: heute })
      setFehler(null)
      await aktualisiere()
      zeigeMeldung('Budget angelegt.')
    },
    onError: (e: ApiFehler) => setFehler(e.message),
  })

  const eintragen = useMutation({
    mutationFn: () => {
      const cents = centsAusEingabe(eintrag.betrag)
      if (cents === null || cents === 0)
        throw new ApiFehler('validation', 'Bitte einen Betrag angeben.', 0)
      return schicke<{ entry: Eintrag }>('POST', `/api/budget/${eintragFuer ?? ''}/entries`, {
        amountCents: cents,
        note: eintrag.note.trim() === '' ? null : eintrag.note.trim(),
        occurredOn: eintrag.occurredOn,
      })
    },
    onSuccess: async () => {
      setEintragFuer(null)
      setEintrag({ betrag: '', note: '', occurredOn: heute })
      setFehler(null)
      await aktualisiere()
      zeigeMeldung('Eingetragen.')
    },
    onError: (e: ApiFehler) => setFehler(e.message),
  })

  const loeschen = useMutation({
    mutationFn: (werte: { budgetId: string; entryId: string }) =>
      schicke('DELETE', `/api/budget/${werte.budgetId}/entries/${werte.entryId}`),
    onSuccess: aktualisiere,
    onError: (e: ApiFehler) => {
      zeigeMeldung(e.message)
    },
  })

  useFab(
    darfVerwalten
      ? {
          icon: 'plus',
          label: 'Budget anlegen',
          onClick: () => {
            setFehler(null)
            setNeuOffen(true)
          },
        }
      : null,
  )

  return (
    <>
      <div className="seitenkopf">
        <div>
          <h1>Budget</h1>
          <p>Ein Umschlag je Monat — jeder Monat fängt bei null an.</p>
        </div>
      </div>

      {liste.isPending ? <p className="unterzeile">Wird geladen …</p> : null}

      {!liste.isPending && budgets.length === 0 ? (
        <Empty
          titel="Noch kein Budget"
          text="Ein Budget ist eine Grenze für einen Monat — zum Beispiel Lebensmittel."
        />
      ) : null}

      {budgets.map((b) => {
        const uebrig = b.limitCents - b.spentCents
        return (
          <Card
            key={b.id}
            titel={b.name}
            aktion={
              <IconButton
                name={offen === b.id ? 'chevron-down' : 'chevron-right'}
                label={offen === b.id ? 'Einträge schließen' : 'Einträge zeigen'}
                onClick={() => setOffen(offen === b.id ? null : b.id)}
              />
            }
          >
            <p className="budget-zahlen">
              <strong>{centsFormatiert(b.spentCents)}</strong> von {centsFormatiert(b.limitCents)}
            </p>
            {/* ⚠️ Ein natives `progress`, kein nachgebauter Balken: es bringt
                Rolle und Werte für Screenreader mit, und der Anteil ist ein
                Attribut - also kein Maß im Markup. Ein `div` mit
                `role="progressbar"` und drei aria-Attributen wäre dieselbe
                Sache von Hand, nur mit mehr Stellen zum Vergessen. */}
            <progress
              className={uebrig < 0 ? 'balken balken--drueber' : 'balken'}
              value={balkenAnteil(b.spentCents, b.limitCents)}
              max={1}
              aria-label={`${b.name}: ${centsFormatiert(b.spentCents)} von ${centsFormatiert(b.limitCents)}`}
            />
            <p className="unterzeile">
              {uebrig < 0
                ? `${centsFormatiert(-uebrig)} über der Grenze`
                : `${centsFormatiert(uebrig)} übrig`}
              {' · '}
              {datumKurz(b.periodStart)} – {datumKurz(b.periodEnd)}
            </p>

            {offen === b.id ? (
              <>
                {einzeln.isPending ? <p className="unterzeile">Wird geladen …</p> : null}
                {einzeln.data && einzeln.data.entries.length === 0 ? (
                  <p className="unterzeile">In diesem Monat noch nichts eingetragen.</p>
                ) : null}
                {einzeln.data && einzeln.data.entries.length > 0 ? (
                  <ul className="liste liste--gruppe">
                    {einzeln.data.entries.map((e) => (
                      <ListItem
                        key={e.id}
                        titel={centsFormatiert(e.amountCents)}
                        unterzeile={`${datumKurz(e.occurredOn)}${e.note ? ` · ${e.note}` : ''}`}
                        folgend={
                          darfEintragen ? (
                            <IconButton
                              name="trash"
                              label="Eintrag löschen"
                              onClick={() => loeschen.mutate({ budgetId: b.id, entryId: e.id })}
                            />
                          ) : undefined
                        }
                      />
                    ))}
                  </ul>
                ) : null}
                {darfEintragen ? (
                  <Button
                    variant="tonal"
                    icon="plus"
                    onClick={() => {
                      setFehler(null)
                      setEintragFuer(b.id)
                    }}
                  >
                    Ausgabe eintragen
                  </Button>
                ) : null}
              </>
            ) : null}
          </Card>
        )
      })}

      <Sheet offen={neuOffen} titel="Neues Budget" onSchliessen={() => setNeuOffen(false)}>
        <form
          className="stack-4"
          onSubmit={(e) => {
            e.preventDefault()
            anlegen.mutate()
          }}
        >
          <TextField
            id="budget-name"
            label="Name"
            wert={neu.name}
            onWert={(w) => setNeu((n) => ({ ...n, name: w }))}
            hilfe="Lebensmittel, Ausflüge, Kleidung …"
            required
            autoFocus
          />
          <TextField
            id="budget-grenze"
            label="Grenze in Euro"
            wert={neu.grenze}
            onWert={(w) => setNeu((n) => ({ ...n, grenze: w }))}
            inputMode="decimal"
            required
          />
          <TextField
            id="budget-start"
            label="Beginnt am"
            type="date"
            wert={neu.startsOn}
            onWert={(w) => setNeu((n) => ({ ...n, startsOn: w }))}
          />
          {/* ⚠️ Frei über den Knöpfen, nicht an einem Feld: der Server nennt
              kein Feld, also darf die Oberfläche auch keines behaupten. Zuvor
              hing die Meldung fest am E-Mail-Feld - eine Meldung über die
              Telefonnummer markierte damit die E-Mail als ungültig. */}
          {fehler ? (
            <p className="fehler" role="alert">
              {fehler}
            </p>
          ) : null}
          <div className="blatt__fuss-inline">
            <Button variant="text" onClick={() => setNeuOffen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" laeuft={anlegen.isPending}>
              Anlegen
            </Button>
          </div>
        </form>
      </Sheet>

      <Sheet
        offen={eintragFuer !== null}
        titel="Ausgabe eintragen"
        onSchliessen={() => setEintragFuer(null)}
      >
        <form
          className="stack-4"
          onSubmit={(e) => {
            e.preventDefault()
            eintragen.mutate()
          }}
        >
          <TextField
            id="eintrag-betrag"
            label="Betrag in Euro"
            wert={eintrag.betrag}
            onWert={(w) => setEintrag((n) => ({ ...n, betrag: w }))}
            inputMode="decimal"
          />
          <TextField
            id="eintrag-datum"
            label="Datum"
            type="date"
            wert={eintrag.occurredOn}
            onWert={(w) => setEintrag((n) => ({ ...n, occurredOn: w }))}
          />
          <TextField
            id="eintrag-zweck"
            label="Wofür (optional)"
            wert={eintrag.note}
            onWert={(w) => setEintrag((n) => ({ ...n, note: w }))}
          />
          {/* ⚠️ Frei über den Knöpfen, nicht an einem Feld: der Server nennt
              kein Feld, also darf die Oberfläche auch keines behaupten. Zuvor
              hing die Meldung fest am E-Mail-Feld - eine Meldung über die
              Telefonnummer markierte damit die E-Mail als ungültig. */}
          {fehler ? (
            <p className="fehler" role="alert">
              {fehler}
            </p>
          ) : null}
          <div className="blatt__fuss-inline">
            <Button variant="text" onClick={() => setEintragFuer(null)}>
              Abbrechen
            </Button>
            <Button type="submit" laeuft={eintragen.isPending}>
              Eintragen
            </Button>
          </div>
        </form>
      </Sheet>
    </>
  )
}
